import { Box, Grid } from '@mui/material'
import { useEffect, useState } from 'react'

import { fromFrequencyCode, fromPOSCode, getDateFromString } from '../utils'
import { DataPanel } from './DataPanel'
import { DataTable } from './DataTable'

type DataEntry = { key: string; value: string; private: boolean }

interface PhysicianClaimDetailsProps {
    data: any
}

export const PhysicianClaimDetails = ({ data }: PhysicianClaimDetailsProps) => {
    const [occurrences, setOccurrences] = useState<DataEntry[]>([])
    const [values, setValues] = useState<DataEntry[]>([])
    const [isClaimPrivateDataAvailable, setIsClaimPrivateDataAvailable] = useState(false)
    const [dateOfAdmission, setDateOfAdmission] = useState('')
    const [statementPeriod, setStatementPeriod] = useState<{ start: string; end: string }>({
        start: '',
        end: '',
    })

    useEffect(() => {
        if (!data.claim) {
            setIsClaimPrivateDataAvailable(true)
            return
        }

        const privateData = data.claim?.privateData
        if (privateData) {
            setIsClaimPrivateDataAvailable(true)
            if (data.claim?.publicData.dateOfAdmission) {
                setDateOfAdmission(
                    getDateFromString(data.claim?.publicData.dateOfAdmission).toLocaleDateString(),
                )
            }
            const statementPeriodMatches = (data.claim?.publicData.statementPeriod ?? '').match(
                /^(\d{8})\s*-\s*(\d{8})$/,
            )
            if (statementPeriodMatches && statementPeriodMatches.length === 3) {
                const start = getDateFromString(statementPeriodMatches[1]).toLocaleDateString()
                const end = getDateFromString(statementPeriodMatches[2]).toLocaleDateString()
                setStatementPeriod({ start, end })
            }

            if (data.claim && data.claim.privateData && data.claim.privateData.occurrences) {
                let first = true
                const _occurrences: DataEntry[] = []
                for (const occurrence of data.claim.privateData.occurrences) {
                    let key = ''
                    if (first) {
                        key = 'Occurrences Codes'
                        first = false
                    }
                    _occurrences.push({
                        key,
                        value: `${occurrence.code} ${getDateFromString(
                            occurrence.date,
                        ).toLocaleDateString()}`,
                        private: true,
                    })
                }
                setOccurrences(_occurrences)
            }
        }
        if (data.claim && data.claim.publicData.values) {
            let first = true
            const _values: DataEntry[] = []
            for (const value of data.claim.publicData.values) {
                let key = ''
                if (first) {
                    key = 'Value Codes'
                    first = false
                }
                _values.push({ key, value: `${value.code} ${value.price}`, private: false })
            }
            setValues(_values)
        }
    }, [data])

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const typeOfCareCode = (data.claim?.publicData.billType ?? '').charAt(0)
    const placeOfService = `${fromPOSCode(data.claim?.privateData?.placeOfService)} (${
        data.claim?.privateData?.placeOfService
    })`
    const claimDetailsContent = (
        <>
            <DataTable
                isPrivateDataAvailable={isClaimPrivateDataAvailable}
                data={[
                    {
                        key: 'Patient Account Number',
                        value: data.claim?.publicData.claimNumber,
                        private: false,
                    },
                    { key: 'Date of Admission', value: dateOfAdmission, private: false },
                    {
                        key: 'Statement Period Start',
                        value: `${statementPeriod.start} - ${statementPeriod.end}`,
                        private: false,
                    },
                    { key: 'Place of Service', value: placeOfService, private: true },
                ]}
            />
            <DataTable isPrivateDataAvailable={isClaimPrivateDataAvailable} data={occurrences} />
            <Box
                sx={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            />
            <DataTable isPrivateDataAvailable={isClaimPrivateDataAvailable} data={values} />
        </>
    )

    const claimsAdmissionSummary = (
        <>
            <DataTable
                isPrivateDataAvailable={isClaimPrivateDataAvailable}
                data={[
                    {
                        key: 'Principal Diagnosis Code',
                        value: data.claim?.publicData.principalDiagnosisCode,
                        private: false,
                    },
                    {
                        key: 'Secondary Diagnosis Codes',
                        value: data.claim?.publicData.secondaryDiagnosisCodes,
                        private: false,
                    },
                    {
                        key: 'Number of Covered Days',
                        value: data.claim?.publicData.numberOfCoveredDays,
                        private: false,
                    },
                    {
                        key: 'Frequency Code',
                        value: fromFrequencyCode(data.claim?.privateData?.frequencyCode),
                        private: true,
                    },
                ]}
            />
            <Box
                sx={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            />
        </>
    )

    return (
        <Grid container spacing={4}>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel minHeight={150} title="Claim Details" content={claimDetailsContent} />
            </Grid>
            <Grid item container lg={6} md={12} xs={12} spacing={4}>
                <Grid item xs={12}>
                    <DataPanel minHeight={150} title="" content={claimsAdmissionSummary ?? <></>} />
                </Grid>
            </Grid>
        </Grid>
    )
}
